import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import api, { formatApiError } from "@/lib/api";
import { Loader2, ChevronLeft, ImagePlus, X, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

export default function CreateIdeaPage() {
  const navigate = useNavigate();
  const { language } = useI18n();
  const L = (ar, he, en) => language === "ar" ? ar : language === "he" ? he : en;

  const [form, setForm] = useState({ title: "", description: "", category: "", image_url: null });
  const [busy, setBusy] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB image"); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 3 || form.description.trim().length < 10) {
      toast.error(L("الرجاء تعبئة العنوان والوصف.", "אנא מלא כותרת ותיאור.", "Please fill title and description.")); return;
    }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category || null,
        image_url: form.image_url || null,
      };
      const { data } = await api.post("/ideas", payload);
      toast.success(L("تمت مشاركة فكرتك!", "הרעיון פורסם!", "Idea shared!"));
      if (data && data.id) navigate(`/ideas/${data.id}`, { replace: true });
      else navigate("/ideas", { replace: true });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <MobileShell
      topBar={
        <div className="h-14 flex items-center gap-2 px-4">
          <button
            onClick={() => navigate(-1)}
            data-testid="cidea-back-btn"
            className="w-9 h-9 rounded-full bg-[var(--te-input-bg)] hover:bg-[#E5E7F3] flex items-center justify-center"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="w-9 h-9 rounded-full bg-[#FEF3C7] flex items-center justify-center">
            <Lightbulb size={17} className="text-[#F59E0B]" />
          </div>
          <h1 className="font-extrabold text-[20px] text-[var(--te-text)] flex-1">
            {L("فكرة جديدة", "רעיון חדש", "New idea")}
          </h1>
        </div>
      }
    >
      <form onSubmit={submit} noValidate className="px-4 pt-4 pb-6 space-y-5">
        <div>
          <label className="te-label">{L("عنوان الفكرة", "כותרת הרעיון", "Idea title")}</label>
          <input
            required
            className="te-input"
            value={form.title}
            onChange={onChange("title")}
            maxLength={160}
            data-testid="cidea-title"
            placeholder={L("مثال: نقاط شحن للدراجات الكهربائية", "לדוגמה: עמדות טעינה לאופניים", "e.g., E-bike charging stations")}
            dir="auto"
          />
        </div>
        <div>
          <label className="te-label">{L("الوصف", "תיאור", "Description")}</label>
          <textarea
            required
            className="te-input min-h-[120px] resize-y"
            value={form.description}
            onChange={onChange("description")}
            maxLength={6000}
            data-testid="cidea-description"
            placeholder={L("اشرح المشكلة والحل المقترح…", "תאר את הבעיה והפתרון המוצע…", "Describe the problem and your proposed solution…")}
            dir="auto"
          />
        </div>
        <div>
          <label className="te-label">{L("الفئة (اختياري)", "קטגוריה (אופציונלי)", "Category (optional)")}</label>
          <input
            className="te-input"
            value={form.category}
            onChange={onChange("category")}
            data-testid="cidea-category"
            placeholder={L("نقل، بيئة، شباب…", "תחבורה, סביבה, נוער…", "transport, environment, youth…")}
            dir="auto"
          />
        </div>
        <div>
          <label className="te-label">{L("صورة (اختياري)", "תמונה (אופציונלי)", "Image (optional)")}</label>
          {form.image_url ? (
            <div className="relative inline-block">
              <img src={form.image_url} alt="" className="rounded-2xl max-h-56 border border-[var(--te-divider)]" />
              <button type="button" onClick={() => setForm((f) => ({ ...f, image_url: null }))} className="absolute top-2 right-2 bg-white rounded-full p-1.5 shadow-sm border border-[var(--te-divider)]">
                <X size={14} />
              </button>
            </div>
          ) : (
            <label className="te-btn-secondary inline-flex items-center gap-2 cursor-pointer" data-testid="cidea-upload-image">
              <ImagePlus size={16} /> {L("ارفع صورة", "העלה תמונה", "Upload image")}
              <input type="file" accept="image/*" className="hidden" onChange={onFileChange} />
            </label>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="te-btn-secondary flex-1 justify-center"
            data-testid="cidea-cancel"
          >
            {L("إلغاء", "ביטול", "Cancel")}
          </button>
          <button
            type="submit"
            disabled={busy}
            className="te-btn-primary flex-1 justify-center"
            data-testid="cidea-submit"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : null}
            {L("شارك", "פרסם", "Share")}
          </button>
        </div>
      </form>
    </MobileShell>
  );
}
